import React, {useState, useEffect, useReducer} from 'react';
import {Page} from "./models/Page";
import {Comment} from "./models/Comment";
import Markdown from "react-markdown";
import rehypeExternalLinks from 'rehype-external-links'
import {ToggleName, Action, defaultReducer, State} from "./State"
import SignIn, {SignInFetchers} from "./SignIn";
import {Paginator} from "./models/Paginator";
import {Fetcher} from "./models/Fetcher";
import Header from "./Header";
import {User} from "./models/User";

export type StatePage = State & {
    page: Page
    commentText: string | null
    commentPaginator: Paginator<Comment>,
    commentPoster: Fetcher;
}

export type ActionPage = Action |
    { type: 'set-comment', payload: string } |
    { type: 'prepend-comment', payload: Comment } |
    { type: 'append-comments', payload: Comment[] }
    ;

function PageShow(props: { page: any, comments: [any], comments_next_page_token: string, comment_maximum_length: number, user: User }) {
    const initialState: StatePage = {
        page: Object.assign(new Page(), props.page),
        user: props.user,
        toggles: new Set<ToggleName>(),
        emailVerifying: "",
        codeVerifying: "",
        commentText: null,
        commentPaginator: new Paginator<Comment>(Comment,
            props.comments.map((c) => Object.assign(new Comment(), c) as Comment), props.page.comment_count,
            "/comments/get.json", "comments", props.comments_next_page_token,
            null,
            (err:any) => {}),
        commentPoster: new Fetcher( `/comments/create.json`),
        signInFetchers: new SignInFetchers()
    };

    const reducer = (state: StatePage, action: ActionPage): StatePage => {
        let defaultResult = defaultReducer(state, action as Action);
        if(defaultResult) {return {...state, ...defaultResult};}
        switch (action.type) {
            case 'set-comment': {
                return {...state, commentText: (action.payload)};
            }
            case 'prepend-comment': {
                let page = state.page;
                let comments = state.commentPaginator.records || [];
                if(comments[0] && comments[0].id == action.payload.id) {
                    comments[0] = action.payload;
                } else {
                    page.comment_count = (page.comment_count || 0) + 1;
                    comments = [action.payload, ...comments];
                }
                state.commentPaginator.records = comments;
                return {
                    ...state,
                    page: page,
                    commentText: "",
                }; // prepend a comment
            }
            case 'append-comments': {
                state.commentPaginator.append(action.payload);
                return {
                    ...state,
                    commentText: ""
                }; // prepend a comment
            }
        }
        return state;
    }

    const [state, dispatch] = useReducer(reducer, initialState);
    const signIn = new SignIn(state, dispatch);

    useEffect(() => {
        console.log("useEffect")
        window.history.replaceState(null, "", "/")
        state.commentPoster.onChange = () => {  dispatch({type: 'change'}) };
        state.commentPaginator.onChange = () => {  dispatch({type: 'change'}) };
    });

    const postComment = () => {
        state.commentPoster.post({text: state.commentText, page_id: state.page.id},(json:any) => {
            let comment = Object.assign(new Comment(), json);
            dispatch({type: 'prepend-comment', payload: comment});
            dispatch({type: 'toggle-set', payload: 'commenting', value: false});
        })
    }

    const moreComments = () => {
        state.commentPaginator.more();
    }

    return (
        <>
            <Header signIn={signIn} />
            <div className="row my-3">
                <div className="md-12 text-center">
                    <h1>{state.page.title}</h1>
                </div>
            </div>
            <div className="row my-3">
                <div className="md-12 text-center">
                    <img src={state.page.imageUrl()} alt={state.page.title} className="img-fluid rounded loading"/>
                </div>
            </div>
            <div className="row mt-4 justify-content-md-center">
                <div className="col-lg-8">
                    <Markdown rehypePlugins={[[rehypeExternalLinks, {target: '_blank'}]]}>
                        {state.page.caption}
                    </Markdown>
                </div>
            </div>

            <div className="mt-3 d-flex justify-content-between">
                <div className="">
                    <h2>
                        Comments
                        {(state.page.comment_count || 0) > 0 &&
                            <>{`(${state.page.comment_count})`}</>
                        }
                    </h2>
                    <footer className="blockquote-footer">from Wang lovers</footer>
                </div>

                <div className="mb-2">
                    <button className="btn btn-primary" type="button" onClick={() => {
                        state.commentPoster.reset();
                        dispatch({type: 'toggle', payload: 'commenting'})
                    }}>
                        + Comment
                    </button>
                </div>
            </div>
            {state.toggles.has('commenting') &&
                <>
                    <div className="row my-2">
                        <div className="md-12 text-center">
                            <h2>New Comment</h2>
                        </div>
                    </div>
                    { state.commentPoster.hasErrors() && <>
                        <div className="row my-2">
                            <div className="md-12 text-center alert alert-danger">
                                {state.commentPoster.errors().map((error) => {
                                    return <>{error}<br/></>
                                })}
                            </div>
                        </div>
                    </>}
                    {signIn.isSignedIn() && <div className="row">
                        <div className="md-12 text-center">
                        <textarea name="comment" rows={3} cols={50}
                                  onChange={(e) => {
                                      dispatch({type: "set-comment", payload: e.target.value})
                                  }}>
                            {state.commentText}
                        </textarea>
                        </div>
                    </div>}
                    {(state.commentText || '').length > props.comment_maximum_length  && <div className="row">
                        <div className="md-12 text-center alert alert-warning">
                            Too Long! {(state.commentText || '').length} characters
                        </div>
                    </div>}
                    {!signIn.isSignedIn() && <div className="row">
                        <div className="md-12 text-center">
                            Please verify your email address before posting.
                        </div>
                    </div>}
                    {signIn.handle()}
                    {signIn.isSignedIn() && <div className="row my-3">
                        <div className="md-12 text-center">
                            <button className={"btn btn-primary btn-lg"} disabled={state.commentPoster.inProgress}
                                    onClick={() => {
                                        postComment()
                                    }}>
                                Post Comment
                            </button>
                        </div>
                    </div>}
                </>
            }
            {
                state.commentPaginator.records.map((comment) => {
                    return <div className="card mb-3">
                        <div className="col-md-12 card-body">
                            <p className="card-text">{comment.text}</p>
                            <footer className="blockquote-footer">{comment.user_display_name}</footer>
                        </div>
                    </div>
                })
            }

            {state.commentPaginator.hasMoreRecords() &&
                <div className="mt-3 d-flex justify-content-between">
                    <div className="">
                    </div>

                    <div className="mb-2">
                        <button className="btn btn-primary" type="button" onClick={() => {
                            moreComments()
                        }} disabled={state.commentPaginator.isInProgress()}>
                            + More
                        </button>
                    </div>
                </div>
            }
            <div className="row mb-2 mt-5">
                <div className="md-12 text-center">
                    <img src={state.page.imageUrl("thumb")} alt={state.page.title} className="img-fluid page-thumbnail rounded loading"/>
                </div>
            </div>
            <div className="row my-2">
                <div className="md-12 text-center">
                <h3>Create your own page!</h3>
                    You may replace this page or create a new one from scratch. It's up to you!
                </div>
            </div>
            <div className="d-flex justify-content-center mt-3 mb-4">
                <div className="mx-2">
                    <a href={'/pages/new?name=' + encodeURIComponent(state.page.name)}>
                        <button className={"btn btn-primary btn-lg"}>
                            Replace Page
                        </button>
                    </a>
                </div>
                <div className="mx-2">
                    <a href={`${Constants.get('HOME_URL')}/pages/new`}>
                        <button className={"btn btn-primary btn-lg"}>
                            Create Page
                        </button>
                    </a>
                </div>
            </div>
            <div className="d-flex justify-content-center mt-3 mb-4">
                <div className="mx-2">
                    <a href={Constants.get('HOME_URL')}>
                        View More Wang Lovers
                    </a>
                </div>
            </div>
        </>
    );
}

export default PageShow;
